import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value);
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('pt-BR');
};

const calcularDataVencimento = (dataAplicacao, diasVencimento) => {
  if (!diasVencimento) return null;
  const data = new Date(dataAplicacao + 'T00:00:00');
  data.setDate(data.getDate() + diasVencimento);
  return data.toISOString().split('T')[0];
};

export function FixedIncomeTableRows({ transacoes, onEdit, onConcluir, onDelete }) {
  if (!transacoes || transacoes.length === 0) {
    return (
      <tr>
        <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
          Nenhuma transação cadastrada
        </td>
      </tr>
    );
  }

  return transacoes.map((transacao) => {
    const dataVencimento = calcularDataVencimento(transacao.data_aplicacao, transacao.dias_vencimento);
    
    return (
      <tr key={transacao.id} className="hover:bg-gray-50">
        {/* Tipo de Investimento */}
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
          {transacao.tipo_investimento}
        </td>

        {/* Data da Aplicação */}
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {formatDate(transacao.data_aplicacao)}
        </td>

        {/* Dias de Vencimento */}
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {transacao.dias_vencimento ? `${transacao.dias_vencimento} dias` : '—'}
        </td>

        {/* Data de Vencimento */}
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {dataVencimento ? formatDate(dataVencimento) : '—'}
        </td>

        {/* Valor Investido */}
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
          {formatCurrency(transacao.valor_investido)}
        </td>

        {/* Rentabilidade */}
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {transacao.rentabilidade ? `${transacao.rentabilidade.toFixed(2)}%` : '—'}
        </td>

        {/* Retorno Bruto Estimado */}
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
          {transacao.retorno_bruto ? formatCurrency(transacao.retorno_bruto) : '—'}
        </td>

        {/* Status */}
        <td className="px-6 py-4 whitespace-nowrap">
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            transacao.status === 'Aberto' 
              ? 'bg-blue-100 text-blue-800'
              : transacao.status === 'Concluído Automaticamente'
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-800'
          }`}>
            {transacao.status}
          </span>
        </td>

        {/* Ações */}
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
          <div className="flex space-x-2">
            {transacao.status === 'Aberto' && (
              <button
                onClick={() => onConcluir(transacao.id)}
                className="text-green-600 hover:text-green-900 p-1 rounded"
                title="Concluir manualmente"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            )}
            
            {transacao.status === 'Aberto' && (
              <button
                onClick={() => onEdit(transacao)}
                className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                title="Editar"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            )}
            
            <button
              onClick={() => onDelete(transacao.id)}
              className="text-red-600 hover:text-red-900 p-1 rounded"
              title="Excluir"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </td>
      </tr>
    );
  });
}

export function useFixedIncomeActions(carregarDados) {
  const handleConcluir = async (transacaoId) => {
    if (!window.confirm('Deseja concluir esta transação manualmente? O retorno será recalculado até a data de hoje.')) {
      return;
    }

    try {
      const response = await axios.post(`${API}/fixed-income/transacoes/${transacaoId}/concluir`, {}, getAuthHeaders());
      toast.success(`Transação concluída! Retorno: ${formatCurrency(response.data.retorno_bruto || 0)}`);
      carregarDados();
    } catch (error) {
      toast.error('Erro ao concluir transação');
      console.error(error);
    }
  };

  const handleDelete = async (transacaoId) => {
    if (!window.confirm('Deseja excluir esta transação?')) {
      return;
    }

    try {
      await axios.delete(`${API}/fixed-income/transacoes/${transacaoId}`, getAuthHeaders());
      toast.success('Transação excluída');
      carregarDados();
    } catch (error) {
      toast.error('Erro ao excluir transação');
      console.error(error);
    }
  };

  return { handleConcluir, handleDelete };
}
